import { useRef, useState } from 'react';
// @ts-ignore
import { UAParser } from 'ua-parser-js';
import type { IDevice, IOS } from 'ua-parser-js';

interface UseUAParserProps {
  userAgent?: string;
}

// DeX/데스크톱 모드의 안드로이드 태블릿은 UA가 Linux 데스크톱으로 바뀌어 device.type이
// 비어 온다. UA와 무관한 하드웨어 사실인 터치 지원 여부로 태블릿으로 보정한다. (MR-23980)
// 현재 기기의 UA를 파싱할 때만 적용해야 하므로 명시적 userAgent 파싱에는 쓰지 않는다.
export function correctDesktopModeTablet(device: IDevice, os: IOS): IDevice {
  if (
    !device.type &&
    os.name === INFO_NAME.OSName.LINUX &&
    typeof navigator !== 'undefined' &&
    navigator.maxTouchPoints > 0
  ) {
    device.type = INFO_NAME.DeviceType.TABLET;
  }
  return device;
}

export function useUAParser(props?: UseUAParserProps) {
  const { userAgent } = props || {};

  const parser = useRef(UAParser(userAgent).withFeatureCheck());

  const [browser] = useState(() => parser.current.browser);
  const [cpu] = useState(() => parser.current.cpu);
  const [device] = useState(() =>
    userAgent ? parser.current.device : correctDesktopModeTablet(parser.current.device, parser.current.os),
  );
  const [os] = useState(() => parser.current.os);
  const [engine] = useState(() => parser.current.engine);

  return {
    browser,
    cpu,
    device,
    os,
    engine,
  };
}

export const INFO_NAME = {
  BrowserName: {
    CHROME: 'Chrome',
    ['EDGE']: 'Edge',
    ['SAFARI']: 'Safari',
    ['FIREFOX']: 'Firefox',
    ['OPERA']: 'Opera',
    ['MOBILE_CHROME']: 'Mobile Chrome',
    ['MOBILE_SAFARI']: 'Mobile Safari',
    ['MOBILE_FIREFOX']: 'Mobile Firefox',
    ['ANDROID_BROWSER']: 'Android Browser',
  },
  CPUArch: {
    IA32: 'ia32',
    AMD64: 'amd64',
    IA64: 'ia64',
    ARM: 'arm',
    ARM64: 'arm64',
    ARMHF: 'armhf',
    '68K': '68k',
    AVR: 'avr',
    IRIX: 'irix',
    IRIX64: 'irix64',
    MIPS: 'mips',
    MIPS64: 'mips64',
    PPC: 'ppc',
    SPARC: 'sparc',
    SPARC64: 'sparc64',
  },
  DeviceType: {
    MOBILE: 'mobile',
    TABLET: 'tablet',
    SMARTTV: 'smarttv',
    CONSOLE: 'console',
    WEARABLE: 'wearable',
    EMBEDDED: 'embedded',
  },
  DeviceVendor: {
    APPLE: 'Apple',
    SAMSUNG: 'Samsung',
    HUAWEI: 'Huawei',
    XIAOMI: 'Xiaomi',
    OPPO: 'OPPO',
    VIVO: 'Vivo',
    REALME: 'Realme',
    LENOVO: 'Lenovo',
    LG: 'LG',
  },
  EngineName: {
    AMAYA: 'Amaya',
    BLINK: 'Blink',
    EDGEHTML: 'EdgeHTML',
    FLOW: 'Flow',
    GECKO: 'Gecko',
    GOANNA: 'Goanna',
    ICAB: 'iCab',
    LIBWEB: 'LibWeb',
    KHTML: 'KHTML',
    LINKS: 'Links',
    LYNX: 'Lynx',
    NETFRONT: 'NetFront',
    NETSURF: 'NetSurf',
    PRESTO: 'Presto',
    TASMAN: 'Tasman',
    TRIDENT: 'Trident',
    W3M: 'w3m',
    WEBKIT: 'WebKit',
  },
  OSName: {
    WINDOWS: 'Windows',
    LINUX: 'Linux',
    MACOS: 'macOS',
    IOS: 'iOS',
    ANDROID: 'Android',
  },
  DeviceModel: {
    I_PHONE: 'iPhone',
    I_PAD: 'iPad',
  },
};
